import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/Store";
import {
  completeDevTicket,
  getAllDevTickets,
} from "../../../redux/requests/Dev-TicketsRequests";
import { logout } from "../../../redux/requests/OperatorRequests";
import { useNavigate } from "react-router-dom";
import "./Developer.css";
import { Link } from "react-router-dom";

interface IDeveloperTicketsProps {}

const DeveloperTickets: FC<IDeveloperTicketsProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { devTickets, isLoading, devTicketActionLoading, error } = useSelector(
    (state: RootState) => state.devTicket,
  );

  const { operator } = useSelector((state: RootState) => state.operator);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevTicketId, setSelectedDevTicketId] = useState<string | null>(
    null,
  );
  const [developerComment, setDeveloperComment] = useState("");

  useEffect(() => {
    dispatch(getAllDevTickets());
  }, [dispatch]);

  const openCompleteModal = (id: string) => {
    setSelectedDevTicketId(id);
    setDeveloperComment("");
    setIsModalOpen(true);
  };

  const closeCompleteModal = () => {
    setIsModalOpen(false);
    setSelectedDevTicketId(null);
    setDeveloperComment("");
  };

  const onCompleteConfirm = async () => {
    if (!selectedDevTicketId) return;

    await dispatch(
      completeDevTicket(selectedDevTicketId, {
        developerComment,
      }),
    );

    closeCompleteModal();
    dispatch(getAllDevTickets());
  };

  const onLogout = async () => {
    await dispatch(logout());
    navigate("/");
  };

  const getStatusClassName = (status: string) => {
    if (status === "Completed") {
      return "developer-tickets__status-badge developer-tickets__status-badge--completed";
    }

    return "developer-tickets__status-badge developer-tickets__status-badge--in-process";
  };

  return (
    <div className="developer-tickets-page">
      <div className="developer-tickets-page__header">
        <div>
          <h1 className="developer-tickets-page__title">Панель программиста</h1>
          <p className="developer-tickets-page__subtitle">
            Добро пожаловать{operator?.name ? `, ${operator.name}` : ""}
          </p>
        </div>

        <button
          className="developer-tickets-page__logout-button"
          onClick={onLogout}
        >
          Выйти
        </button>
      </div>

      <div className="developer-tickets-card">
        <div className="developer-tickets-card__topbar">
          <div>
            <h2 className="developer-tickets-card__title">
              Технические заявки
            </h2>
            <p className="developer-tickets-card__subtitle">
              Все заявки от операторов по тикетам и звонкам
            </p>
          </div>
        </div>

        {isLoading && (
          <p className="developer-tickets-card__info-text">Загрузка...</p>
        )}
        {error && <p className="developer-tickets-card__error-text">{error}</p>}

        {!isLoading && devTickets.length === 0 && (
          <p className="developer-tickets-card__info-text">Заявок пока нет</p>
        )}

        {!isLoading && devTickets.length > 0 && (
          <div className="developer-tickets-table-wrapper">
            <table className="developer-tickets-table">
              <thead>
                <tr>
                  <th className="developer-tickets-table__th">Вопрос</th>
                  <th className="developer-tickets-table__th">Оператор</th>
                  <th className="developer-tickets-table__th">
                    Комментарий оператора
                  </th>
                  <th className="developer-tickets-table__th">
                    Комментарий тех. отдела
                  </th>
                  <th className="developer-tickets-table__th">Телефон</th>
                  <th className="developer-tickets-table__th">Тикет</th>
                  <th className="developer-tickets-table__th">Статус</th>
                  <th className="developer-tickets-table__th">Дата создания</th>
                  <th className="developer-tickets-table__th">Действие</th>
                  <th className="developer-tickets-table__th">Дата закрытия</th>
                </tr>
              </thead>
              <tbody>
                {devTickets.map((item) => (
                  <tr key={item.id} className="developer-tickets-table__row">
                    <td className="developer-tickets-table__td">
                      <div className="developer-tickets-table__main-text">
                        {item.question}
                      </div>
                    </td>

                    <td className="developer-tickets-table__td">
                      <div className="developer-tickets-table__secondary-text">
                        {item.operator?.name || "-"}
                      </div>
                    </td>

                    <td className="developer-tickets-table__td">
                      <div className="developer-tickets-table__secondary-text">
                        {item.operatorComment || "-"}
                      </div>
                    </td>

                    <td className="developer-tickets-table__td">
                      <div className="developer-tickets-table__secondary-text">
                        {item.developerComment || "-"}
                      </div>
                    </td>

                    <td className="developer-tickets-table__td">
                      {item.phoneNumber || "-"}
                    </td>

                    <td className="developer-tickets-table__td">
                      {item.ticketId ? (
                        <Link
                          to={`/admin/tickets/dialog/${item.ticketId}`}
                          className="developer-tickets-table__ticket-link"
                        >
                          {item.ticketId}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="developer-tickets-table__td">
                      <span className={getStatusClassName(item.status)}>
                        {item.status === "Completed" ? "Завершено" : "В работе"}
                      </span>
                    </td>

                    <td className="developer-tickets-table__td">
                      {new Date(item.createdAt).toLocaleString()}
                    </td>

                    <td className="developer-tickets-table__td">
                      {item.status === "InProcess" ? (
                        <button
                          className="developer-tickets-table__complete-button"
                          onClick={() => openCompleteModal(item.id)}
                          disabled={devTicketActionLoading}
                        >
                          Завершить
                        </button>
                      ) : (
                        <span className="developer-tickets-table__done-text">
                          Готово
                        </span>
                      )}
                    </td>

                    <td className="developer-tickets-table__td">
                      {item.completedAt
                        ? new Date(item.completedAt).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="developer-tickets-modal-overlay">
          <div className="developer-tickets-modal">
            <h3 className="developer-tickets-modal__title">Завершить заявку</h3>
            <p className="developer-tickets-modal__text">
              Оставьте комментарий о проделанной работе
            </p>

            <textarea
              value={developerComment}
              onChange={(e) => setDeveloperComment(e.target.value)}
              className="developer-tickets-modal__textarea"
            />

            <div className="developer-tickets-modal__actions">
              <button
                className="developer-tickets-modal__cancel-button"
                onClick={closeCompleteModal}
                disabled={devTicketActionLoading}
              >
                Отмена
              </button>

              <button
                className="developer-tickets-modal__save-button"
                onClick={onCompleteConfirm}
                disabled={devTicketActionLoading}
              >
                {devTicketActionLoading ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperTickets;
